import type { ReactNode } from "react";
import { createContext, useContext } from "react";

import { createPortal } from "react-dom";

import { WidgetTitleEndSlotContext } from "~/v3-poc/components/Widget";

// ---------------------------------------------------------------------------
// Workspace toolbar slot
//
// The layout toolbar belongs visually to the workspace tab strip (top-right of
// the header row), but its state and handlers belong to whichever engine tab
// is mounted — restart, add-widget, and the settings disclosure all come from
// that tab's controller, which lives below `Tabs.Content`. This
// module bridges the two: `Workspace.pres` publishes the header element, and
// each engine tab renders its toolbar into it without lifting any state.
//
// This is the one module in the Layout Labs tree permitted to call
// `createPortal` — see the carve-out in `productionUiBoundary.test.ts`. The
// rule it is exempted from targets *overlay* portals, which used to need a
// bespoke portal root plus a theme-marker attribute to keep the Radix
// appearance. The target here is an ordinary element already inside the root
// `<Theme>`, so there is no theming boundary to cross.
// ---------------------------------------------------------------------------

const WorkspaceToolbarSlotContext = createContext<HTMLElement | null>(null);

export const WorkspaceToolbarSlotProvider =
  WorkspaceToolbarSlotContext.Provider;

export interface WorkspaceToolbarPortalProps {
  children: ReactNode;
}

/**
 * Renders `children` into the workspace header slot when one has been
 * published, and in place otherwise.
 *
 * The in-place fallback is the contract for rendering an engine tab outside a
 * workspace — which is exactly what the tab presenter unit tests do. It also
 * covers the first paint inside a workspace: the slot is captured by a
 * callback ref, so it is `null` for one render before the portal takes over.
 */
export const WorkspaceToolbarPortal = ({
  children,
}: WorkspaceToolbarPortalProps) => {
  const slot = useContext(WorkspaceToolbarSlotContext);
  return slot ? createPortal(children, slot) : children;
};

// Bridges tab-owned controls into the left edge of the board header.
const WorkspaceTitleLeadingSlotContext = createContext<HTMLElement | null>(
  null,
);

export const WorkspaceTitleLeadingSlotProvider =
  WorkspaceTitleLeadingSlotContext.Provider;

/**
 * Renders `children` into the slot left of the workspace title when one has
 * been published, and in place otherwise (tab presenter tests, first paint).
 */
export const WorkspaceTitleLeadingPortal = ({
  children,
}: WorkspaceToolbarPortalProps) => {
  const slot = useContext(WorkspaceTitleLeadingSlotContext);
  return slot ? createPortal(children, slot) : children;
};

// ---------------------------------------------------------------------------
// Widget title slot
//
// The same bridge, one level down: a widget's title suffix belongs beside the
// kind label in its header bar, but the value lives in the widget body that
// renders below it. `Widget` publishes the element; this fills it.
//
// Widgets outside this tree portal into that slot themselves. This wrapper
// exists because the boundary test bans `createPortal` in the workspace tree
// except in this one file.
// ---------------------------------------------------------------------------

/**
 * Renders `children` into the host widget's title, and in place otherwise.
 *
 * The in-place fallback is what widget presenter tests render against, and it
 * also covers the first paint: the slot is captured by a callback ref, so it is
 * `null` for one render before the portal takes over.
 */
export const WidgetTitleEndPortal = ({
  children,
}: WorkspaceToolbarPortalProps) => {
  const slot = useContext(WidgetTitleEndSlotContext);
  return slot ? createPortal(children, slot) : children;
};
